/**
 * Router con History API y carga diferida de páginas (code splitting).
 * Cada página devuelve su elemento raíz y una función opcional de limpieza.
 */
import type { Role } from '@manitype/core';
import { clear, h } from '../lib/dom';
import { t, type MessageKey } from '../lib/i18n';
import { getState } from './store';
import { announceRoute } from './embed';

export interface PageContext {
  params: Record<string, string>;
  query: URLSearchParams;
}

export interface Page {
  el: HTMLElement;
  title?: string;
  destroy?: () => void;
}

export type PageFactory = (ctx: PageContext) => Page | Promise<Page>;

export interface RouteDef {
  path: string;
  load: () => Promise<{ default: PageFactory }>;
  title: MessageKey;
  auth?: boolean;
  role?: Role;
  /** Oculta el pie y reduce la cabecera (pantallas centradas en escribir). */
  focus?: boolean;
}

interface CompiledRoute extends RouteDef {
  regex: RegExp;
  keys: string[];
}

const ROLE_RANK: Record<Role, number> = { user: 0, editor: 1, admin: 2, superadmin: 3 };

let routes: CompiledRoute[] = [];
let outlet: HTMLElement;
let current: Page | null = null;
let navigationId = 0;
const changeListeners = new Set<(path: string, route: RouteDef | null) => void>();

function compile(def: RouteDef): CompiledRoute {
  const keys: string[] = [];
  const pattern = def.path.replace(/\//g, '\\/').replace(/:(\w+)/g, (_, key: string) => {
    keys.push(key);
    return '([^/]+)';
  });
  return { ...def, keys, regex: new RegExp(`^${pattern}\\/?$`) };
}

function match(pathname: string): { route: CompiledRoute; params: Record<string, string> } | null {
  for (const route of routes) {
    const m = route.regex.exec(pathname);
    if (!m) continue;
    const params: Record<string, string> = {};
    route.keys.forEach((key, i) => {
      params[key] = decodeURIComponent(m[i + 1] ?? '');
    });
    return { route, params };
  }
  return null;
}

export function onRouteChange(listener: (path: string, route: RouteDef | null) => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (to === location.pathname + location.search) return void render();
  if (options.replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  void render();
}

/** Vuelve a renderizar la ruta actual (p. ej. tras iniciar sesión o cambiar el idioma). */
export function reload(): void {
  void render();
}

async function render(): Promise<void> {
  const id = ++navigationId;
  const found = match(location.pathname);
  const query = new URLSearchParams(location.search);
  current?.destroy?.();
  current = null;

  for (const listener of changeListeners) listener(location.pathname, found?.route ?? null);

  if (!found) {
    const notFound = (await import('../features/notFound')).default;
    if (id !== navigationId) return;
    return mount(await notFound({ params: {}, query }), t('notFound.title'));
  }

  const { route, params } = found;
  const user = getState().user;
  if ((route.auth || route.role) && !user) {
    navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    return;
  }
  if (route.role && user && ROLE_RANK[user.role] < ROLE_RANK[route.role]) {
    const forbidden = (await import('../features/notFound')).default;
    if (id !== navigationId) return;
    return mount(await forbidden({ params: { reason: 'forbidden' }, query }), t('admin.noPermission'));
  }

  outlet.setAttribute('aria-busy', 'true');
  try {
    const module = await route.load();
    if (id !== navigationId) return;
    const page = await module.default({ params, query });
    if (id !== navigationId) return page.destroy?.();
    mount(page, page.title ?? t(route.title));
  } catch (error) {
    console.error(error);
    if (id !== navigationId) return;
    mount({ el: h('section', { class: 'container page-error' }, h('h1', null, t('common.error')), h('button', { class: 'btn', onClick: () => location.reload() }, t('common.retry'))) }, t('common.error'));
  } finally {
    outlet.removeAttribute('aria-busy');
  }
}

function mount(page: Page, title: string): void {
  current = page;
  clear(outlet);
  outlet.appendChild(page.el);
  document.title = `${title} · ManiType`;
  // Si estamos dentro de un marco, la barra del navegador es la de fuera:
  // se le dice dónde estamos para que la refleje (ver embed.ts).
  announceRoute(document.title);
  window.scrollTo({ top: 0 });
  const heading = outlet.querySelector('h1');
  if (heading) {
    heading.setAttribute('tabindex', '-1');
    if (!outlet.querySelector('[data-autofocus]')) heading.focus({ preventScroll: true });
  }
}

function isInternalLink(anchor: HTMLAnchorElement, event: MouseEvent): boolean {
  return (
    anchor.origin === location.origin &&
    !anchor.hasAttribute('download') &&
    anchor.target !== '_blank' &&
    !anchor.dataset.external &&
    event.button === 0 &&
    !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
  );
}

export function startRouter(target: HTMLElement, defs: RouteDef[]): void {
  outlet = target;
  routes = defs.map(compile);
  window.addEventListener('popstate', () => void render());
  document.addEventListener('click', (event) => {
    const anchor = (event.target as Element | null)?.closest?.('a');
    if (!anchor || !isInternalLink(anchor, event)) return;
    event.preventDefault();
    navigate(anchor.pathname + anchor.search + anchor.hash);
  });
  void render();
}
